import React from 'react';
import { ArrowLeft, ExternalLink, Users, Target, BookOpen, Heart, Building2, Award, CheckCircle, ArrowRight } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { partnerOrganizations } from '../data/partners';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const colorSchemes = {
  blue: {
    gradient: 'from-blue-600 via-blue-700 to-indigo-800',
    light: 'bg-blue-50',
    accent: 'text-blue-600',
    accentBg: 'bg-blue-100',
    border: 'border-blue-200',
    button: 'from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700'
  },
  rose: {
    gradient: 'from-rose-600 via-pink-600 to-rose-800',
    light: 'bg-rose-50',
    accent: 'text-rose-600',
    accentBg: 'bg-rose-100',
    border: 'border-rose-200',
    button: 'from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700'
  },
  emerald: {
    gradient: 'from-emerald-600 via-teal-600 to-emerald-800',
    light: 'bg-emerald-50',
    accent: 'text-emerald-600',
    accentBg: 'bg-emerald-100',
    border: 'border-emerald-200',
    button: 'from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
  },
  amber: {
    gradient: 'from-amber-600 via-orange-600 to-amber-800',
    light: 'bg-amber-50',
    accent: 'text-amber-600',
    accentBg: 'bg-amber-100',
    border: 'border-amber-200',
    button: 'from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700'
  },
  violet: {
    gradient: 'from-violet-600 via-purple-600 to-violet-800',
    light: 'bg-violet-50',
    accent: 'text-violet-600',
    accentBg: 'bg-violet-100',
    border: 'border-violet-200',
    button: 'from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700'
  },
  cyan: {
    gradient: 'from-cyan-600 via-teal-600 to-cyan-800',
    light: 'bg-cyan-50',
    accent: 'text-cyan-600',
    accentBg: 'bg-cyan-100',
    border: 'border-cyan-200',
    button: 'from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700'
  }
};

const focusIcons = [Target, Users, BookOpen, Heart];

const PartnerPage = () => {
  const { partnerId } = useParams();
  
  // Find the partner by ID
  const partner = Object.values(partnerOrganizations).find(p => p.id === partnerId);
  
  if (!partner) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div id="main-content" role="main" tabIndex={-1} className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-slate-800 mb-4">Partner Not Found</h1>
          <Link to="/" className="text-teal-600 hover:text-teal-700">Return to Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const colors = colorSchemes[partner.color] || colorSchemes.blue;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section id="main-content" role="main" tabIndex={-1} className={`relative pt-20 pb-16 bg-gradient-to-br ${colors.gradient}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
          <Link to="/#organization" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Partners
          </Link>
          
          <div className="max-w-4xl">
            <span className="inline-block px-4 py-2 bg-white/20 text-white rounded-full text-sm font-semibold mb-6">
              DCELC Partner Organization
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
              {partner.name}
            </h1>
            {partner.acronym && (
              <p className="text-2xl text-white/80 mb-6">({partner.acronym})</p>
            )}
            <p className="text-xl text-white/90 leading-relaxed">
              {partner.description}
            </p>
            
            {partner.website && (
              <a
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-white text-slate-800 rounded-xl font-semibold hover:bg-white/90 transition-colors"
              >
                Visit Official Website
                <ExternalLink className="w-5 h-5" />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`${colors.light} rounded-2xl p-8 border ${colors.border}`}>
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-14 h-14 ${colors.accentBg} rounded-xl flex items-center justify-center`}>
                <Target className={`w-7 h-7 ${colors.accent}`} />
              </div>
              <h2 className="text-2xl font-bold text-slate-800">Our Mission</h2>
            </div>
            <p className="text-lg text-slate-700 leading-relaxed">
              {partner.mission}
            </p>
            {partner.vision && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="font-semibold text-slate-800 mb-2">Our Vision</h3>
                <p className="text-slate-600">{partner.vision}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Focus Areas */}
      {partner.focus && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-800 mb-4">Our Focus Areas</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Key areas where {partner.acronym || partner.name} makes an impact in the early childhood education community.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {partner.focus.map((item, index) => {
                const Icon = focusIcons[index % focusIcons.length];
                return (
                  <div key={index} className={`bg-white rounded-2xl p-8 shadow-lg border ${colors.border} hover:shadow-xl transition-shadow duration-300`}>
                    <div className={`w-14 h-14 ${colors.accentBg} rounded-xl flex items-center justify-center mb-6`}>
                      <Icon className={`w-7 h-7 ${colors.accent}`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Impact Stats (if available) */}
      {partner.impact?.stats && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-800 mb-4">{partner.impact.title || "Our Impact"}</h2>
              {partner.impact.description && (
                <p className="text-slate-600 max-w-2xl mx-auto">{partner.impact.description}</p>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {partner.impact.stats.map((stat, index) => (
                <div key={index} className={`text-center p-8 rounded-2xl ${colors.light} border ${colors.border}`}>
                  <p className={`text-4xl font-bold ${colors.accent} mb-2`}>{stat.value}</p>
                  <p className="text-slate-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services/Involvement (if available) */}
      {(partner.services || partner.involvement) && (
        <section className={`py-16 bg-gradient-to-br ${colors.gradient}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-white mb-4">
                {partner.services ? "Our Services" : "Our Involvement"}
              </h2>
            </div>
            
            <div className="space-y-4">
              {(partner.services || partner.involvement).map((item, index) => (
                <div key={index} className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <CheckCircle className="w-6 h-6 text-white/80 flex-shrink-0" />
                  <span className="text-white">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Membership/Events (if available) */}
      {(partner.membership || partner.events) && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {partner.membership && (
                <div className={`${colors.light} rounded-2xl p-8 border ${colors.border}`}>
                  <Award className={`w-12 h-12 ${colors.accent} mb-6`} />
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">{partner.membership.title}</h3>
                  <p className="text-slate-600 mb-6">{partner.membership.description}</p>
                  {partner.website && (
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r ${colors.button} text-white rounded-xl font-semibold transition-all duration-300`}
                    >
                      Join Now
                      <ArrowRight className="w-5 h-5" />
                    </a>
                  )}
                </div>
              )}
              
              {partner.events && (
                <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
                  <Building2 className="w-12 h-12 text-slate-600 mb-6" />
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">Events & Activities</h3>
                  <ul className="space-y-3">
                    {partner.events.map((event, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <CheckCircle className={`w-5 h-5 ${colors.accent}`} />
                        <span className="text-slate-700">{event}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Leadership (if available) */}
      {partner.leadership && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">Leadership</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {partner.leadership.executiveDirector && (
                <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                  <div className={`w-20 h-20 ${colors.accentBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Users className={`w-10 h-10 ${colors.accent}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">{partner.leadership.executiveDirector}</h3>
                  <p className="text-slate-600">Executive Director</p>
                </div>
              )}
              {partner.leadership.programCoordinator && (
                <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                  <div className={`w-20 h-20 ${colors.accentBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Users className={`w-10 h-10 ${colors.accent}`} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800">{partner.leadership.programCoordinator}</h3>
                  <p className="text-slate-600">Program Coordinator</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">Connect with {partner.acronym || partner.name}</h2>
          <p className="text-lg text-slate-600 mb-8">
            Learn more about how {partner.acronym || partner.name} is strengthening early childhood education in Washington, DC.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {partner.website && (
              <a
                href={partner.website}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r ${colors.button} text-white rounded-xl font-semibold shadow-lg transition-all duration-300`}
              >
                Visit Website
                <ExternalLink className="w-5 h-5" />
              </a>
            )}
            <Button
              onClick={() => window.location.href = '/#contact'}
              variant="outline"
              className="px-8 py-4 text-slate-700 border-slate-300 hover:bg-slate-50"
            >
              Contact DCELC
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnerPage;
